export type ListingBadgeKey = "TOP" | "VIP" | "URGENT";

export interface ListingBadgeTheme {
  key: ListingBadgeKey;
  emoji: string;
  labelKey: string;
  labelFallback: string;
  badgeGradient: string;
  cardBorder: string;
  priceColor: string;
  legendBg: string;
  accentText: string;
  accentBorder: string;
  accentBg: string;
}

export const LISTING_BADGE_THEMES: Record<ListingBadgeKey, ListingBadgeTheme> =
  {
    URGENT: {
      key: "URGENT",
      emoji: "🔥",
      labelKey: "listing.badges.urgent.label",
      labelFallback: "HOT",
      badgeGradient: "bg-gradient-to-r from-red-600 to-red-500",
      cardBorder: "border-red-300",
      priceColor: "text-red-700",
      legendBg: "bg-red-50",
      accentText: "text-red-700",
      accentBorder: "border-red-400",
      accentBg: "bg-red-50",
    },
    VIP: {
      key: "VIP",
      emoji: "🚀",
      labelKey: "listing.badges.vip.label",
      labelFallback: "VIP",
      badgeGradient: "bg-gradient-to-r from-orange-500 to-amber-400",
      cardBorder: "",
      priceColor: "text-orange-700",
      legendBg: "bg-amber-50",
      accentText: "text-orange-700",
      accentBorder: "border-orange-400",
      accentBg: "bg-orange-50",
    },
    TOP: {
      key: "TOP",
      emoji: "💎",
      labelKey: "listing.badges.top.label",
      labelFallback: "PREMIUM",
      badgeGradient: "bg-gradient-to-r from-blue-600 to-sky-400",
      cardBorder: "",
      priceColor: "text-blue-700",
      legendBg: "bg-blue-50",
      accentText: "text-blue-600",
      accentBorder: "border-blue-400",
      accentBg: "bg-blue-50",
    },
  };

/** Promotion priority, strongest first: HOT (URGENT) → VIP → PREMIUM (TOP). Keys stay as the backend sends them. */
export const LISTING_BADGE_ORDER: ListingBadgeKey[] = ["URGENT", "VIP", "TOP"];

// Loose on purpose: listing shapes differ (API DTO, mapped Listing, map pins).
type PromoFlags = object | null | undefined;
const flags = (p: PromoFlags) => (p ?? {}) as { is_urgent?: boolean; is_vip?: boolean; is_top?: boolean };

/** The strongest promotion a listing carries (a listing can have several). */
export function listingBadgeKey(p: PromoFlags): ListingBadgeKey | null {
  const f = flags(p);
  if (f.is_urgent) return "URGENT";
  if (f.is_vip) return "VIP";
  if (f.is_top) return "TOP";
  return null;
}

/** Sort rank: 0 = HOT, 1 = VIP, 2 = TOP, 3 = not promoted. */
export function listingPromoRank(p: PromoFlags): number {
  const key = listingBadgeKey(p);
  return key ? LISTING_BADGE_ORDER.indexOf(key) : LISTING_BADGE_ORDER.length;
}
